import React, { FC } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { EMSAttendee } from 'models/enrichmentData';
import { AttendeePersonalInfoItem, AttendeeInfoTitle, AttendeeInfoAnswer } from './sharedComponent';

interface AttendeePersonalInfoProps {
  fullName: string;
  employment: any;
  location: string;
  site: string;
  github: string;
  twitter: string;
  facebook: string;
  linkedin: string;
  attendee: EMSAttendee;
}

const AttendeePersonalInfoContainer = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: auto;
`;

const InfoLink = styled.a`
  color: #5C8DF3;
`;

const CapitalizedAttendeeInfoAnswer = styled(AttendeeInfoAnswer)`
  text-transform: capitalize;
`;

const AttendeePersonalInfo:FC<AttendeePersonalInfoProps> = ({
  fullName,
  employment,
  location,
  site,
  github,
  twitter,
  facebook,
  linkedin,
  attendee,
}) => {
  const { t } = useTranslation();

  return (
    <AttendeePersonalInfoContainer>
      {(attendee?.name || fullName) && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.attendee_name', 'Name')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>{attendee?.name || fullName}</AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {(attendee?.company || employment?.name) && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.employment_name', 'Employment')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>{attendee?.company || employment?.name}</AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {employment?.role && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.employment_role', 'Role')}</AttendeeInfoTitle>
          <CapitalizedAttendeeInfoAnswer>{employment?.role}</CapitalizedAttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {employment?.seniority && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.employment_seniority', 'Seniority')}</AttendeeInfoTitle>
          <CapitalizedAttendeeInfoAnswer>{employment?.seniority}</CapitalizedAttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {location && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.location', 'Location')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>{location}</AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {site && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.site', 'Site')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>
            <InfoLink href={site} target="_blank">
              {site}
            </InfoLink>
          </AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {github && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.github', 'Github')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>
            <InfoLink href={`https://github.com/${github}`} target="_blank">
              {`github.com/${github}`}
            </InfoLink>
          </AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {twitter && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.twitter', 'Twitter')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>
            <InfoLink href={`https://twitter.com/${twitter}`} target="_blank">
              {`twitter.com/${twitter}`}
            </InfoLink>
          </AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {facebook && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.facebook', 'Facebook')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>
            <InfoLink href={`https://facebook.com/${facebook}`} target="_blank">
              {`facebook.com/${facebook}`}
            </InfoLink>
          </AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
      {linkedin && (
        <AttendeePersonalInfoItem>
          <AttendeeInfoTitle>{t('attendee_enrichment.linkedin', 'Linkedin')}</AttendeeInfoTitle>
          <AttendeeInfoAnswer>
            <InfoLink href={`https://linkedin.com/${linkedin}`} target="blank">
              {`linkedin.com/${linkedin}`}
            </InfoLink>
          </AttendeeInfoAnswer>
        </AttendeePersonalInfoItem>
      )}
    </AttendeePersonalInfoContainer>
  );
};

export default AttendeePersonalInfo;
